/**
 * The single place the app chooses a data implementation.
 *
 * `VITE_DATA_SOURCE=live` wires the generated-client adapter
 * (`./generated-adapter.ts` over `@match-point/api-client-ts`); anything else
 * is the in-memory fake. Screens depend on `ApiClient` and on the query keys;
 * they never see a transport, and an ESLint rule keeps raw `fetch` out of the
 * rest of the app.
 */

import { inject, type App, type InjectionKey } from 'vue'

import type { ApiClient, SessionContext } from './contract'
import { createFakeApiClient } from './fake'
import { createGeneratedAdapter } from './generated-adapter'

export const API_CLIENT: InjectionKey<ApiClient> = Symbol('match-point.api-client')

export type DataSource = 'fake' | 'live'

export function configuredDataSource (): DataSource {
  return import.meta.env.VITE_DATA_SOURCE === 'live' ? 'live' : 'fake'
}

export interface ApiClientOptions {
  /**
   * The current ID token, from the auth seam. Required in live mode — the
   * adapter attaches it per request, so a token refresh needs no re-wiring.
   */
  getToken?: () => Promise<string | undefined>
  /**
   * Whether the session is a staff-pool one, asked per call (ADR 0016). With
   * `VITE_STAFF_API_BASE_URL` set, a staff session's requests go to the staff API
   * and every other session's to the customer API: each API's authorizer trusts
   * one pool and refuses the other's tokens before the service runs.
   */
  isStaff?: () => boolean
}

export function createApiClient (context: SessionContext, options: ApiClientOptions = {}): ApiClient {
  if (configuredDataSource() === 'live') {
    const baseUrl = import.meta.env.VITE_API_BASE_URL
    // Deliberately loud. A silent fallback to the fake would let a "live" build
    // ship looking like it worked, which is the failure mode this seam exists
    // to prevent.
    if (!baseUrl) {
      throw new Error('VITE_DATA_SOURCE=live requires VITE_API_BASE_URL.')
    }
    if (!options.getToken) {
      throw new Error(
        'VITE_DATA_SOURCE=live requires a token getter. Pass { getToken } from the auth client — ' +
          'every route except signup refuses an unauthenticated caller.'
      )
    }
    const customer = createGeneratedAdapter({ baseUrl, getToken: options.getToken, context })
    const staffBaseUrl = import.meta.env.VITE_STAFF_API_BASE_URL
    if (!staffBaseUrl || !options.isStaff) return customer

    const staff = createGeneratedAdapter({ baseUrl: staffBaseUrl, getToken: options.getToken, context })
    return routeBySession(customer, staff, options.isStaff)
  }
  return createFakeApiClient(context)
}

/**
 * One `ApiClient` that sends each call to the staff adapter when the session is a
 * staff one and to the customer adapter otherwise, decided at call time so a
 * sign-in or sign-out needs no re-wiring. A transport choice only: the server
 * still decides what a token may do, whichever API it arrives through.
 */
export function routeBySession (customer: ApiClient, staff: ApiClient, isStaff: () => boolean): ApiClient {
  return new Proxy(customer, {
    get: (_target, property) => {
      const chosen = (isStaff() ? staff : customer) as unknown as Record<PropertyKey, unknown>
      const value = chosen[property]
      return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(chosen) : value
    }
  })
}

export function provideApiClient (app: App, client: ApiClient): void {
  app.provide(API_CLIENT, client)
}

export function useApiClient (): ApiClient {
  const client = inject(API_CLIENT)
  if (!client) throw new Error('No ApiClient provided. Call provideApiClient() in main.ts.')
  return client
}

export * from './contract'
export { scenario, setScenario, SCENARIOS, SCENARIO_LABELS, type Scenario } from './scenario'
export { createPublicApi, type PublicApi } from './public'
